import type { EngineError, EngineEvent, MediaTrack, PlayerEngine, StreamCandidate } from './engine';

export type PlaybackErrorCode = 'network' | 'unsupported' | 'media' | 'forbidden' | 'not-found' | 'stalled' | 'no-start';

export interface PlaybackError {
  code: PlaybackErrorCode;
  status?: number;
  detail?: string;
}

export type PlayerStatus =
  | { state: 'idle' }
  | { state: 'loading'; candidate: number; candidates: number }
  /** `soundOff`: muted because another app (a call) took the sound; the viewer can unmute. */
  | { state: 'playing'; soundOff?: true }
  | { state: 'buffering' }
  /** `byOtherApp`: the phone paused live TV for another app, even muted; Play goes live again. */
  | { state: 'paused'; byOtherApp?: true }
  | { state: 'ended' }
  /** `dropped`: the stream had played in this session (vs. never connected). */
  | { state: 'reconnecting'; attempt: number; maxAttempts: number; delayMs: number; dropped: boolean; reason: PlaybackError }
  | { state: 'switching'; candidate: number; candidates: number }
  | { state: 'error'; error: PlaybackError };

export interface PlaybackTimers {
  set(callback: () => void, ms: number): unknown;
  clear(handle: unknown): void;
  now(): number;
}

export interface ControllerOptions {
  /**
   * Reconnect attempts on the primary URL after playback was established
   * (a dropped stream), before moving to alternates.
   */
  maxAttempts?: number;
  /** Attempts on the primary URL when it has never played in this session. */
  initialAttempts?: number;
  /** Reconnect attempts on each alternate URL. */
  alternateAttempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  /** Continuous playback after which the attempt counter resets. */
  stableAfterMs?: number;
  /** Live playback with no time progress for this long counts as a failure. */
  stallTimeoutMs?: number;
  /** Time allowed from load until the first frame. */
  startTimeoutMs?: number;
  /** Whether the page is on screen; a browser pause is only reconnected while it is. Default: always. */
  isVisible?: () => boolean;
  /** After `returnToLive`, time for live TV to play again before it reconnects. */
  returnTimeoutMs?: number;
  timers?: PlaybackTimers;
  random?: () => number;
}

export interface PlaybackProblem {
  /** Milliseconds since the session started. */
  atMs: number;
  candidate: number;
  error: PlaybackError;
}

export interface PlaybackMetrics {
  /** Load request to first `playing`, for the current session. */
  firstFrameMs: number | null;
  reconnects: number;
  candidate: StreamCandidate | null;
  /** Most recent failures of this session, newest last (at most 10). */
  problems: PlaybackProblem[];
}

const realTimers: PlaybackTimers = {
  set: (callback, ms) => setTimeout(callback, ms),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  now: () => performance.now(),
};

/**
 * Engine-independent playback policy:
 * 1. Retry the current URL with exponential backoff (1 s, 2 s, 4 s, ... with
 *    ±20% jitter): up to `maxAttempts` (5) times once the stream had played
 *    (a dropped connection), `initialAttempts` (2) times when it never
 *    started, so a dead channel reaches its error screen in under a minute.
 * 2. Then move to the next candidate URL (alternate format or source).
 * 3. When every candidate is exhausted, report a final error.
 * Format errors skip straight to the next candidate. Live streams that stop
 * advancing, never start, or "end" are treated as dropped connections.
 */
/** A busy relay (503) is asked again every 10 s, about a minute in all (#234). */
const BUSY_ATTEMPTS = 6;
const BUSY_DELAY_MS = 10_000;

export class PlaybackController {
  private readonly maxAttempts: number;
  private readonly initialAttempts: number;
  private readonly alternateAttempts: number;
  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  private readonly stableAfterMs: number;
  private readonly stallTimeoutMs: number;
  private readonly startTimeoutMs: number;
  private readonly isVisible: () => boolean;
  private readonly returnTimeoutMs: number;
  private readonly timers: PlaybackTimers;
  private readonly random: () => number;
  private readonly listeners = new Set<(status: PlayerStatus) => void>();
  private readonly unsubscribe: () => void;

  private candidates: StreamCandidate[] = [];
  private index = 0;
  private attempt = 0;
  private live = true;
  /** Live latency when the viewer paused live TV (null: watching live). */
  private latencyAtPause: number | null = null;
  /** The viewer pressed pause (cleared by resume, go live and a new session). */
  private viewerPaused = false;
  /**
   * Live TV paused by the browser, not the viewer, even after muting:
   * another app holds the sound and the phone won't let it play.
   */
  private interrupted = false;
  /**
   * The sound was turned off because another app (a call) took it: phones
   * pause video with sound for a call, but let muted video play. Stays on
   * across channels until the viewer unmutes.
   */
  private soundYielded = false;
  private startAt: number | undefined;
  private current: PlayerStatus = { state: 'idle' };
  private session = 0;
  private retryTimer: unknown = null;
  private watchdog: unknown = null;
  private stableTimer: unknown = null;
  private returnTimer: unknown = null;
  private lastPosition = -1;
  private lastProgressAt = 0;
  private loadStartedAt = 0;
  private everPlayed = false;
  /** Whether any candidate produced a picture in this session. */
  private sessionPlayed = false;
  /** A refusal (401/403) after the stream had played was already given its one reload. */
  private refusalRetried = false;
  private lastError: PlaybackError | null = null;
  private sessionStartedAt = 0;
  private metricsState: PlaybackMetrics = { firstFrameMs: null, reconnects: 0, candidate: null, problems: [] };

  constructor(
    private readonly engine: PlayerEngine,
    options: ControllerOptions = {},
  ) {
    this.maxAttempts = options.maxAttempts ?? 5;
    this.initialAttempts = options.initialAttempts ?? 2;
    this.alternateAttempts = options.alternateAttempts ?? 1;
    this.baseDelayMs = options.baseDelayMs ?? 1000;
    this.maxDelayMs = options.maxDelayMs ?? 16_000;
    this.stableAfterMs = options.stableAfterMs ?? 30_000;
    this.stallTimeoutMs = options.stallTimeoutMs ?? 15_000;
    this.startTimeoutMs = options.startTimeoutMs ?? 10_000;
    this.isVisible = options.isVisible ?? (() => true);
    this.returnTimeoutMs = options.returnTimeoutMs ?? 4000;
    this.timers = options.timers ?? realTimers;
    this.random = options.random ?? Math.random;
    this.unsubscribe = engine.on((event) => this.handle(event));
  }

  get status(): PlayerStatus {
    return this.current;
  }

  get metrics(): PlaybackMetrics {
    return this.metricsState;
  }

  subscribe(listener: (status: PlayerStatus) => void): () => void {
    this.listeners.add(listener);
    listener(this.current);
    return () => this.listeners.delete(listener);
  }

  /** Starts a new playback session (also used for channel zapping). */
  play(candidates: StreamCandidate[], options: { live: boolean; startAt?: number }): void {
    this.session++;
    this.clearTimers();
    this.candidates = candidates;
    this.live = options.live;
    this.latencyAtPause = null;
    this.viewerPaused = false;
    this.interrupted = false;
    this.startAt = options.startAt;
    this.index = 0;
    this.attempt = 0;
    this.lastError = null;
    this.sessionPlayed = false;
    this.refusalRetried = false;
    this.sessionStartedAt = this.timers.now();
    this.metricsState = { firstFrameMs: null, reconnects: 0, candidate: null, problems: [] };
    if (candidates.length === 0) {
      this.engine.stop();
      this.fail({ code: 'unsupported', detail: 'no playable URL' });
      return;
    }
    this.loadCurrent();
  }

  /** Manual retry from the error screen: starts over from the first candidate. */
  retry(): void {
    this.play(this.candidates, { live: this.live, startAt: this.startAt });
  }

  pause(): void {
    this.viewerPaused = true;
    // Pausing while the browser had already paused it: the video element sends no new event.
    if (this.interrupted) {
      this.interrupted = false;
      this.set({ state: 'paused' });
    }
    // Paused live TV: the viewer chose to fall behind, so no catch-up; remember where "live" was.
    if (this.live && this.latencyAtPause === null) {
      this.latencyAtPause = this.engine.liveLatency?.() ?? null;
      this.engine.setLiveCatchUp?.(false);
    }
    this.engine.pause();
  }

  resume(): void {
    this.viewerPaused = false;
    // Paused by another app: Play goes back to the live broadcast.
    if (this.interrupted) {
      this.interrupted = false;
      this.goLive();
      return;
    }
    this.engine.play();
  }

  /**
   * Live TV the viewer paused: seconds behind the live broadcast (it plays on
   * from where it was paused). 0 when watching live or not live.
   */
  behindLive(): number {
    if (!this.live || this.latencyAtPause === null) return 0;
    const now = this.engine.liveLatency?.() ?? null;
    return now === null ? 0 : Math.max(0, now - this.latencyAtPause);
  }

  /** Jumps back to the live broadcast after a pause. */
  goLive(): void {
    if (!this.live) return;
    this.latencyAtPause = null;
    this.viewerPaused = false;
    // Jumping ahead pauses progress on purpose; it must not look like a stall.
    this.lastProgressAt = this.timers.now();
    if (this.engine.goLive) this.engine.goLive();
    else this.engine.play();
  }

  /**
   * The page is on screen again (unlocked, back from another app): live TV
   * the browser paused jumps to the live broadcast, and reconnects if it
   * hasn't played again within `returnTimeoutMs` (nothing new was buffered).
   */
  returnToLive(): void {
    if (!this.live || !this.interrupted) return;
    if (this.engine.goLive) this.engine.goLive();
    else this.engine.play();
    const session = this.session;
    if (this.returnTimer !== null) this.timers.clear(this.returnTimer);
    this.returnTimer = this.timers.set(() => {
      this.returnTimer = null;
      if (session === this.session && this.interrupted && this.isVisible()) this.failed({ kind: 'network', detail: 'paused by the browser' });
    }, this.returnTimeoutMs);
  }

  /** The viewer taps "unmute" after another app took the sound. If that app still has it, the phone pauses again and the sound goes off again. */
  unmute(): void {
    if (!this.soundYielded) return;
    this.soundYielded = false;
    this.engine.setMuted?.(false);
    this.engine.play();
    if (this.current.state === 'playing') this.set({ state: 'playing' });
  }

  /** Seeks within VOD. Live streams have nothing to seek to. */
  seek(seconds: number): void {
    if (this.live || !Number.isFinite(seconds)) return;
    // Seeking pauses progress on purpose; it must not look like a stall.
    this.lastProgressAt = this.timers.now();
    if (this.current.state === 'ended') this.set({ state: 'buffering' });
    this.engine.seek(Math.max(0, seconds));
    this.engine.play();
  }

  audioTracks(): MediaTrack[] {
    return this.engine.audioTracks();
  }

  textTracks(): MediaTrack[] {
    return this.engine.textTracks();
  }

  selectAudio(id: string): void {
    this.engine.selectAudio(id);
  }

  selectText(id: string | null): void {
    this.engine.selectText(id);
  }

  stop(): void {
    this.session++;
    this.clearTimers();
    this.engine.stop();
    this.set({ state: 'idle' });
  }

  destroy(): void {
    this.stop();
    this.unsubscribe();
    this.engine.destroy();
    this.listeners.clear();
  }

  private loadCurrent(): void {
    const candidate = this.candidates[this.index] as StreamCandidate;
    this.metricsState.candidate = candidate;
    this.everPlayed = false;
    this.interrupted = false;
    this.lastPosition = -1;
    this.loadStartedAt = this.timers.now();
    this.lastProgressAt = this.loadStartedAt;
    this.set(
      this.index > 0 && this.attempt === 0
        ? { state: 'switching', candidate: this.index + 1, candidates: this.candidates.length }
        : { state: 'loading', candidate: this.index + 1, candidates: this.candidates.length },
    );
    this.engine.load(candidate, { live: this.live, startAt: this.startAt });
    this.engine.play();
    this.armWatchdog();
  }

  private handle(event: EngineEvent): void {
    if (this.current.state === 'idle' || this.current.state === 'error' || this.current.state === 'reconnecting') {
      return;
    }
    switch (event.type) {
      case 'playing':
        if (!this.everPlayed) {
          this.everPlayed = true;
          this.sessionPlayed = true;
          if (this.metricsState.firstFrameMs === null) this.metricsState.firstFrameMs = Math.round(this.timers.now() - this.loadStartedAt);
          this.armStableTimer();
        }
        this.lastProgressAt = this.timers.now();
        this.interrupted = false;
        this.set(this.playingStatus());
        return;
      case 'waiting':
        if (this.everPlayed) this.set({ state: 'buffering' });
        return;
      case 'paused':
        if (!this.everPlayed) return;
        if (!this.live || this.viewerPaused) {
          this.set({ state: 'paused' });
          return;
        }
        // Not playing (connecting, reconnecting, buffering): a pause now isn't another app taking the
        // sound, unless the sound was already given up for one and the phone pauses even muted.
        if (this.current.state !== 'playing' && !this.soundYielded) return;
        // Another app took the sound (a call): phones let muted video play, so mute and carry on.
        if (this.engine.setMuted && !this.engine.isMuted?.()) {
          this.soundYielded = true;
          this.engine.setMuted(true);
          this.engine.play();
          this.set({ state: 'buffering' });
          return;
        }
        // Paused even muted: wait for the viewer (reconnecting can't help while the other app holds it).
        this.interrupted = true;
        this.set({ state: 'paused', byOtherApp: true });
        return;
      case 'time':
        if (event.position !== this.lastPosition) {
          this.lastPosition = event.position;
          this.lastProgressAt = this.timers.now();
          if (this.current.state === 'buffering') this.set(this.playingStatus());
        }
        return;
      case 'tracks':
        this.set({ ...this.current });
        return;
      case 'ended':
        if (this.live) this.failed({ kind: 'network', detail: 'live stream ended' });
        else this.set({ state: 'ended' });
        return;
      case 'error':
        this.failed(event.error);
        return;
    }
  }

  private failed(error: EngineError): void {
    this.clearTimers();
    this.engine.stop();
    const reason = toPlaybackError(error);
    this.lastError = reason;
    // Movies and episodes carry on from where they stopped (reconnects, Try again), not from the start.
    if (!this.live && this.lastPosition > 0) this.startAt = this.lastPosition;
    const problems = this.metricsState.problems;
    problems.push({ atMs: Math.round(this.timers.now() - this.sessionStartedAt), candidate: this.index + 1, error: reason });
    if (problems.length > 10) problems.shift();
    const refused = error.status === 401 || error.status === 403;
    // A stream refused after it played was most likely reconnected with an expired relay pass, or
    // before the provider let go of its previous connection: one reload (with a fresh URL) settles it.
    const retryable = error.kind !== 'unsupported' && error.status !== 404 && (!refused || (this.sessionPlayed && !this.refusalRetried));
    // The relay is busy (all its conversions in use, #234): keep trying every 10 s for about a minute.
    // Only for its conversions: a provider's own 503 (passed through) keeps the usual quick backoff.
    const busy = error.status === 503 && (this.candidates[this.index]?.url ?? '').includes('/transcode?');
    const allowed = busy ? BUSY_ATTEMPTS : this.index > 0 ? this.alternateAttempts : this.sessionPlayed ? this.maxAttempts : this.initialAttempts;
    if (retryable && this.attempt < allowed) {
      this.attempt++;
      if (refused) this.refusalRetried = true;
      this.metricsState.reconnects++;
      const exponential = Math.min(this.maxDelayMs, this.baseDelayMs * 2 ** (this.attempt - 1));
      const delayMs = Math.round((busy ? BUSY_DELAY_MS : exponential) * (0.8 + this.random() * 0.4));
      this.set({ state: 'reconnecting', attempt: this.attempt, maxAttempts: allowed, delayMs, dropped: this.sessionPlayed, reason });
      const session = this.session;
      this.retryTimer = this.timers.set(() => {
        this.retryTimer = null;
        if (session === this.session) this.loadCurrent();
      }, delayMs);
      return;
    }
    if (this.index + 1 < this.candidates.length) {
      this.index++;
      this.attempt = 0;
      this.loadCurrent();
      return;
    }
    this.fail(this.lastError);
  }

  private fail(error: PlaybackError): void {
    this.clearTimers();
    this.set({ state: 'error', error });
  }

  /** Detects a stream that never starts, a live stream that stops advancing, or a movie stuck buffering. */
  private armWatchdog(): void {
    const session = this.session;
    const check = () => {
      this.watchdog = null;
      if (session !== this.session) return;
      const state = this.current.state;
      const now = this.timers.now();
      if (!this.everPlayed && (state === 'loading' || state === 'switching') && now - this.loadStartedAt >= this.startTimeoutMs) {
        this.failed({ kind: 'network', detail: 'no picture before timeout' });
        return;
      }
      // Live TV that stops advancing, or a movie stuck buffering (#238): reconnect, movies at the same spot.
      // A movie the viewer paused is never stuck, even when the engine didn't report the pause.
      const stuck = this.live ? state === 'playing' || state === 'buffering' : state === 'buffering' && !this.viewerPaused;
      if (this.everPlayed && stuck && !this.interrupted && now - this.lastProgressAt >= this.stallTimeoutMs) {
        this.failed({ kind: 'network', detail: 'stream stalled' });
        return;
      }
      this.watchdog = this.timers.set(check, 1000);
    };
    this.watchdog = this.timers.set(check, 1000);
  }

  private armStableTimer(): void {
    const session = this.session;
    this.stableTimer = this.timers.set(() => {
      this.stableTimer = null;
      if (session === this.session && this.current.state !== 'error') {
        this.attempt = 0;
        this.refusalRetried = false;
      }
    }, this.stableAfterMs);
  }

  private clearTimers(): void {
    for (const handle of [this.retryTimer, this.watchdog, this.stableTimer, this.returnTimer]) {
      if (handle !== null) this.timers.clear(handle);
    }
    this.retryTimer = null;
    this.watchdog = null;
    this.stableTimer = null;
    this.returnTimer = null;
  }

  private playingStatus(): PlayerStatus {
    return this.soundYielded ? { state: 'playing', soundOff: true } : { state: 'playing' };
  }

  private set(status: PlayerStatus): void {
    this.current = status;
    for (const listener of this.listeners) listener(status);
  }
}

function toPlaybackError(error: EngineError): PlaybackError {
  if (error.status === 401 || error.status === 403) return { code: 'forbidden', status: error.status, detail: error.detail };
  if (error.status === 404) return { code: 'not-found', status: error.status, detail: error.detail };
  if (error.detail === 'stream stalled') return { code: 'stalled', detail: error.detail };
  if (error.detail === 'no picture before timeout') return { code: 'no-start', detail: error.detail };
  return { code: error.kind, status: error.status, detail: error.detail };
}

export function playbackErrorMessage(error: PlaybackError): string {
  switch (error.code) {
    case 'network':
      return 'The stream could not be reached. Check your connection or try again later.';
    case 'stalled':
      return 'The stream stopped sending video.';
    case 'no-start':
      return 'The stream did not start. The server may be down or too slow.';
    case 'forbidden':
      return 'The provider refused this stream. Your account may have expired or reached its connection limit.';
    case 'not-found':
      return 'This channel is no longer available from the provider.';
    case 'unsupported':
      return "This stream's format isn't supported on this device.";
    case 'media':
      return 'The video could not be decoded.';
  }
}

/** Short, credential-free description of why a stream failed. */
export function describeProblem(error: PlaybackError): string {
  if (error.status) return `server refused (HTTP ${error.status})`;
  switch (error.detail) {
    case 'stream stalled':
      return 'no new video for 15 s';
    case 'no picture before timeout':
      return 'no picture in time';
    case 'live stream ended':
      return 'server closed the stream';
    case 'paused by the browser':
      return 'paused by the browser or another app';
    case 'EarlyEof':
    case 'UnrecoverableEarlyEof':
      return 'connection closed mid-stream';
    case 'ConnectingTimeout':
      return 'connection timed out';
    case 'Exception':
      return 'network failure';
    default:
      return error.detail ? `${error.code}: ${error.detail}` : error.code;
  }
}
